/* eslint-disable complexity */
import {
  build_case_form_sections,
  build_contact_rows,
  build_profile_rows,
  build_stage_timeline,
  build_summary_rows,
  update_onboarding_application_metadata
} from '@/services/onboarding_journey_service';
import {
  build_next_case_from_edit_form,
  build_save_case_payload,
  clear_system_loader,
  dispatch_system_loader,
  find_email_in_direct_keys,
  find_email_in_nested_values,
  find_email_in_string_entries,
  normalize_edit_date,
  parse_persisted_case_edits,
  resolve_case_owner_role_ids,
  resolve_case_template_ids,
  resolve_case_template_name
} from './my_queue_edit_helpers';
import { myQueueEditDropdownMethods } from './my_queue_edit_dropdown_methods';

export const myQueueEditMethods = {
  ...myQueueEditDropdownMethods,
  async open_edit_for_item(item, event) {
    event?.stopPropagation?.();

    const case_item = this.apply_persisted_edits_to_case(item?.case_item);
    if (!case_item?.application_id) return;

    this.queue_action_key = `edit:${case_item.application_id}`;
    dispatch_system_loader('Preparing edit details...');
    this.editing_application_id = case_item.application_id;
    this.edit_error = '';
    const resolved_email = this.resolve_email_value(case_item) || this.resolve_email_value(item) || '';
    this.edit_form = {
      candidate_name: item?.company || case_item.name || '',
      journey: case_item.journey || item?.journey || '',
      template_name: resolve_case_template_name(case_item),
      template_ids: resolve_case_template_ids(case_item),
      onboarding_name: case_item.name || item?.title || '',
      owner_role_id: '',
      owner_role_ids: resolve_case_owner_role_ids(case_item),
      owner_role_name: case_item.onboarding_owner_role_name || '',
      activate_by: normalize_edit_date(case_item.onboarding_activate_by || item?.due || case_item.eta),
      email: resolved_email,
      allow_login: Boolean(resolved_email)
    };
    this.show_edit_modal = true;

    try {
      await Promise.all([
        this.load_available_owner_roles(),
        this.load_edit_dropdown_options(case_item)
      ]);
    } finally {
      this.queue_action_key = '';
      clear_system_loader();
    }
  },
  get_case_edit_storage_key(application_id) {
    const storage_key = `onboarding-case-edit:${application_id}`;
    return storage_key;
  },
  read_persisted_case_edits(application_id) {
    if (!application_id) return {};

    try {
      const raw_value = window.localStorage.getItem(this.get_case_edit_storage_key(application_id));
      const persisted_edits = parse_persisted_case_edits(raw_value);
      return persisted_edits;
    } catch {
      const empty_edits = {};
      return empty_edits;
    }
  },
  write_persisted_case_edits(application_id, payload) {
    if (!application_id) return;
    window.localStorage.setItem(this.get_case_edit_storage_key(application_id), JSON.stringify(payload));
  },
  apply_persisted_edits_to_case(case_item) {
    if (!case_item?.application_id) return case_item;

    const edits = this.read_persisted_case_edits(case_item.application_id);
    if (!Object.keys(edits).length) return case_item;
    const has_activate_by = Object.hasOwn(edits, 'activate_by');
    const next_case = {
      ...case_item,
      ...(edits.onboarding_name ? { name: edits.onboarding_name } : {}),
      ...(edits.journey ? { journey: edits.journey } : {}),
      ...(edits.template_name ? { template_name: edits.template_name, template: edits.template_name } : {}),
      ...(Array.isArray(edits.template_ids) ? { template_ids: edits.template_ids } : {}),
      ...(Array.isArray(edits.owner_role_ids) ? { owner_role_ids: edits.owner_role_ids } : {}),
      ...(edits.owner_role_name ? { onboarding_owner_role_name: edits.owner_role_name } : {}),
      ...(has_activate_by ? { onboarding_activate_by: edits.activate_by } : {})
    };
    return next_case;
  },
  apply_persisted_edits_to_queue_item(item) {
    if (!item?.case_item) return item;

    const next_case = this.apply_persisted_edits_to_case(item.case_item);
    const updated_item = Object.assign({}, item, {
      title: next_case.name || item.title,
      journey: next_case.journey || item.journey,
      case_item: next_case
    });
    return updated_item;
  },
  replace_queue_item_with_case(item, next_case) {
    if (item?.case_item?.application_id !== this.editing_application_id) return item;

    const updated_item = {
      ...item,
      title: next_case.name || item.title,
      journey: next_case.journey || item.journey,
      due: next_case.onboarding_activate_by || item.due,
      case_item: next_case
    };
    return updated_item;
  },
  close_edit_modal() {
    this.show_edit_modal = false;
    this.show_edit_template_dropdown = false;
    this.show_edit_owner_dropdown = false;
    this.edit_template_search = '';
    this.edit_owner_role_search = '';
    this.edit_error = '';
    this.editing_application_id = null;
  },
  resolve_email_value(source, seen = new Set()) {
    if (!source || typeof source !== 'object') return '';
    if (seen.has(source)) return '';
    seen.add(source);
    const direct_keys = ['email', 'contact_email', 'user_email', 'applicant_email', 'candidate_email', 'owner_email', 'invite_email'];
    const direct_email = find_email_in_direct_keys(source, direct_keys);
    if (direct_email) return direct_email;
    const string_entry_email = find_email_in_string_entries(source);
    if (string_entry_email) return string_entry_email;
    const nested_email = find_email_in_nested_values(this, Object.values(source), seen);
    return nested_email;
  },
  async save_case_edits() {
    if (this.is_saving_case_edits) return;

    if (!this.editing_application_id) {
      this.edit_error = 'This onboarding cannot be edited right now.';
      return;
    }

    const onboarding_name = String(this.edit_form.onboarding_name || '').trim();
    const journey = String(this.edit_form.journey || '').trim();
    const template_name = String(this.edit_form.template_name || '').trim();
    const template_ids = this.selected_edit_template_ids();
    const owner_role_ids = this.selected_edit_owner_role_ids();
    const owner_role_id = String(owner_role_ids[0] || this.edit_form.owner_role_id || '').trim();
    const selected_owner_role = this.available_owner_roles.find((role) => String(role.id) === owner_role_id);
    const owner_role_name = this.selected_edit_owner_roles().map((role) => role.name).join(', ')
      || String(selected_owner_role?.name || this.edit_form.owner_role_name || '').trim();
    const activate_by = String(this.edit_form.activate_by || '').trim();

    if (!onboarding_name) {
      this.edit_error = 'Onboarding name is required.';
      return;
    }

    const current_case = this.queue_items.find((item) => item?.case_item?.application_id === this.editing_application_id)?.case_item || {};
    const next_case = build_next_case_from_edit_form(current_case, this.edit_form, owner_role_name, activate_by);

    this.is_saving_case_edits = true;
    dispatch_system_loader('Updating onboarding...');

    try {
      const payload = build_save_case_payload(this.editing_application_id, owner_role_id, activate_by);
      const parsed = await update_onboarding_application_metadata(payload);
      const backend_case = parsed?.data || {};
      next_case.onboarding_owner_role_name = backend_case?.onboarding_owner_role_name || next_case.onboarding_owner_role_name;
      next_case.onboarding_activate_by = backend_case?.onboarding_activate_by || next_case.onboarding_activate_by;
    } catch (error) {
      this.edit_error = error?.response?.data?.message || error?.message || 'Unable to save onboarding details.';
      clear_system_loader();
      this.is_saving_case_edits = false;
      return;
    }

    this.write_persisted_edits_and_refresh(next_case, {
      onboarding_name,
      journey,
      template_name,
      template_ids,
      owner_role_name,
      owner_role_ids,
      activate_by
    });
    this.close_edit_modal();
    clear_system_loader();
    this.is_saving_case_edits = false;
  },
  write_persisted_edits_and_refresh(next_case, persisted_payload) {
    this.write_persisted_case_edits(this.editing_application_id, persisted_payload);
    this.queue_items = this.queue_items.map((item) => this.replace_queue_item_with_case(item, next_case));

    if (this.selected_case?.application_id === this.editing_application_id) {
      this.selected_case = next_case;
      this.summary_rows = build_summary_rows(next_case);
      this.profile_rows = build_profile_rows(next_case);
      this.contact_rows = build_contact_rows(next_case);
      this.stage_timeline = build_stage_timeline(next_case);
      this.case_form_sections = build_case_form_sections(next_case);
      this.agreement_rows = this.build_case_agreement_rows(next_case);
      this.detail_tabs = this.build_detail_tabs(this.case_form_sections, this.agreement_rows);
      this.evidence_items = [];
    }
  }
};
